import { memo, useMemo, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ModalBody, ModalContent, Modal, ModalHeader, ModalFooter } from '@heroui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useUpdateModuleMutation, Module } from '@/entities/Courses';
import { useGetAcademicStaffAllQuery } from '@/entities/AcademicStaff';
import { AppButton } from '@/shared/ui/AppButton';
import { CustomInput, CustomSelect, CustomTextarea } from '@/shared/ui/Fields';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import styles from './EditModuleModal.module.scss';

interface EditModuleModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  courseId: number;
  module: Module | null;
  intakeId: number;
  termNumber: number;
  groupId: number;
  onSuccess?: () => void;
}

interface ModuleFormData {
  title: string;
  description: string;
  moduleTeacherId: string;
  lecturerId: string;
}

const moduleSchema = yup.object({
  title: yup.string().required('Module title is required').min(1).max(255),
  description: yup.string().required('Module description is required').min(1),
  moduleTeacherId: yup.string().required('Module Teacher is required'),
  lecturerId: yup.string().optional(),
}) as yup.ObjectSchema<ModuleFormData>;

export const EditModuleModal = memo(
  ({
    isOpen,
    onOpenChange,
    courseId,
    module,
    intakeId,
    termNumber,
    groupId,
    onSuccess,
  }: EditModuleModalProps) => {
    const { toast } = useToast();
    const [updateModule, { isLoading }] = useUpdateModuleMutation();

    const { data: academicStaffData } = useGetAcademicStaffAllQuery(undefined, { skip: !isOpen });

    const teacherItems = useMemo(
      () =>
        (academicStaffData?.data || [])
          .filter((staff) => !staff.blocked)
          .map((staff) => ({
            key: String(staff.id),
            label: `${staff.firstName} ${staff.lastName}`,
          })),
      [academicStaffData?.data],
    );

    const {
      control,
      handleSubmit,
      formState: { errors },
      reset,
      setValue,
      watch,
    } = useForm<ModuleFormData>({
      resolver: yupResolver(moduleSchema) as any,
      defaultValues: {
        title: '',
        description: '',
        moduleTeacherId: '',
        lecturerId: '',
      },
    });

    useEffect(() => {
      if (module && isOpen) {
        reset({
          title: module.title || '',
          description: module.description || '',
          moduleTeacherId: '',
          lecturerId: '',
        });
      }
    }, [module, isOpen, reset]);

    useEffect(() => {
      if (module && isOpen && teacherItems.length > 0) {
        const moduleTeacherIdRaw = module.moduleTeacher?.id ?? module.moduleTeacherId;
        const lecturerIdRaw =
          module.lecturer?.id ??
          module.lecturerId ??
          ((module as unknown as { lecturer?: { id?: number } }).lecturer?.id ?? undefined);

        const moduleTeacherId = moduleTeacherIdRaw ? String(moduleTeacherIdRaw) : '';
        const lecturerId = lecturerIdRaw ? String(lecturerIdRaw) : '';

        if (moduleTeacherId && teacherItems.some((item) => item.key === moduleTeacherId)) {
          setValue('moduleTeacherId', moduleTeacherId, {
            shouldValidate: false,
            shouldDirty: false,
          });
        }
        if (lecturerId && teacherItems.some((item) => item.key === lecturerId)) {
          setValue('lecturerId', lecturerId, {
            shouldValidate: false,
            shouldDirty: false,
          });
        }
      }
    }, [module, isOpen, teacherItems, setValue]);

    const watchedTitle = watch('title');
    const watchedDescription = watch('description');
    const watchedModuleTeacherId = watch('moduleTeacherId');
    const watchedLecturerId = watch('lecturerId');
    const canSubmit = !!watchedTitle && !!watchedDescription && !!watchedModuleTeacherId;

    const onSubmit = async (data: ModuleFormData) => {
      if (!module) return;

      try {
        const result = await updateModule({
          moduleId: module.id,
          body: {
            intakeId,
            termNumber,
            groupId,
            module: {
              title: data.title,
              description: data.description,
              moduleTeacherId: Number(data.moduleTeacherId),
              lecturerId: data.lecturerId ? Number(data.lecturerId) : undefined,
            },
          },
        }).unwrap();

        toast({
          title: 'Success',
          description: result.message || 'Module updated successfully',
          color: 'success',
        });

        handleClose();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message || 'Failed to update module',
          color: 'danger',
        });
      }
    };

    const handleClose = () => {
      reset({
        title: '',
        description: '',
        moduleTeacherId: '',
        lecturerId: '',
      });
      onOpenChange(false);
    };

    if (!module) return null;

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={handleClose}
        placement="center"
        motionProps={scaleInOut}
        size="2xl"
        backdrop="opaque"
        scrollBehavior="inside"
        classNames={{
          base: 'p-6 flex flex-col gap-5 max-w-[600px]',
          closeButton: styles.closeBtn,
        }}
      >
        <ModalContent>
          {() => (
            <form
              onSubmit={handleSubmit(onSubmit as any)}
              className="flex flex-col max-h-[80vh] overflow-hidden"
            >
              <ModalHeader className={styles.title}>Edit module</ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-6 flex-1 min-h-0 overflow-y-auto">
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      label="Module Title"
                      labelPlacement="outside"
                      placeholder="Enter module title"
                      errorMessage={errors.title?.message}
                      showError={!!errors.title}
                      isRequired
                    />
                  )}
                />

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <CustomTextarea
                      {...field}
                      label="Module Description"
                      labelPlacement="outside"
                      placeholder="Enter module description"
                      errorMessage={errors.description?.message}
                      showError={!!errors.description}
                      isRequired
                    />
                  )}
                />

                <Controller
                  name="moduleTeacherId"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      style="black"
                      label="Module Lead"
                      labelPlacement="outside"
                      placeholder="Select the module lead"
                      items={teacherItems}
                      selectedKeys={field.value ? new Set([field.value]) : new Set()}
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0] as string;
                        field.onChange(selected || '');
                      }}
                      errorMessage={errors.moduleTeacherId?.message}
                      showError={!!errors.moduleTeacherId}
                      isRequired
                      popoverProps={{
                        placement: 'bottom',
                        shouldBlockScroll: false,
                        portalContainer:
                          typeof document !== 'undefined' ? document.body : undefined,
                      }}
                    />
                  )}
                />
                <Controller
                  name="lecturerId"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      style="black"
                      label="Lecturer"
                      labelPlacement="outside"
                      placeholder="Select lecturer"
                      items={teacherItems}
                      selectedKeys={field.value ? new Set([field.value]) : new Set()}
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0] as string;
                        field.onChange(selected || '');
                      }}
                      errorMessage={errors.lecturerId?.message}
                      showError={!!errors.lecturerId}
                      popoverProps={{
                        placement: 'bottom',
                        shouldBlockScroll: false,
                        portalContainer:
                          typeof document !== 'undefined' ? document.body : undefined,
                      }}
                    />
                  )}
                />
              </ModalBody>
              <ModalFooter className="gap-4 pt-6 px-0 shrink-0">
                <AppButton fullWidth color="white" onPress={handleClose} type="button">
                  Cancel
                </AppButton>
                <AppButton
                  fullWidth
                  color="black"
                  type="submit"
                  isLoading={isLoading}
                  disabled={!canSubmit}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmit as any)();
                  }}
                >
                  Update module
                </AppButton>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

EditModuleModal.displayName = 'EditModuleModal';
